import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@mzic/mzic-environments';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import {
  COLLECTION_IDEMPOTENCY_OPERATION,
  CollectionIdempotentOperation,
  ConnectIdempotentOperation,
} from '../idempotency/collection-request-idempotency';
import { CollectionRequestIdempotencyService } from '../idempotency/collection-request-idempotency.service';
import { withCollectionRequestContext } from '../interceptors/collection-request-context';
import {
  AuthInteractionDto,
  CollectionRevisionAuthInteractionDetailDto,
  CollectionRevisionAuthInteractionDetailResponse,
  CollectionRevisionAuthInteractionIdentity,
  CollectionRevisionConnectionRouteIdentity,
  CollectionRevisionConnectionTargetDto,
  CollectionRevisionConnectionTargetResponse,
  ConnectionEntityDto,
  ConnectionRuntimeDto,
  ConnectionRuntimeStatus,
  ConnectionTargetAction,
  ConnectCollectionRevisionTargetRequest,
  SubmitCollectionRevisionAuthInteractionRequest,
  UpdateCollectionRevisionCredentialsRequest,
} from '../interfaces/collection-revision-connection.interface';
import { CollectionRevisionTargetRouteIdentity } from '../collection-flow/collection-revision-route';
import {
  buildCollectionRevisionConnectionAuthInteractionUrl,
  buildCollectionRevisionConnectionAuthResendUrl,
  buildCollectionRevisionConnectionAuthSubmitUrl,
  buildCollectionRevisionConnectionCredentialsUrl,
  buildCollectionRevisionConnectionDeleteUrl,
  buildCollectionRevisionTargetConnectionUrl,
  buildCollectionRevisionTargetConnectionsUrl,
} from './collection-revision-urls';
import { CollectionRevisionWireContractError } from './collection-revision.service';

const CONNECTION_TARGET_ACTIONS = new Set<ConnectionTargetAction>([
  'CONNECT',
  'UPDATE_CREDENTIALS',
  'SUBMIT_AUTH_INTERACTION',
  'RESEND_AUTH_INTERACTION',
  'CANCEL_CONNECTION',
]);

@Injectable({ providedIn: 'root' })
export class CollectionRevisionConnectionService {
  private readonly http = inject(HttpClient);
  private readonly idempotency = inject(CollectionRequestIdempotencyService);
  private readonly apiRoot = `${environment.apiUrl}/api/chordium/v1`;

  getConnectionTarget(
    identity: CollectionRevisionTargetRouteIdentity,
  ): Observable<CollectionRevisionConnectionTargetDto> {
    const exactIdentity = requireTargetIdentity(identity);
    return this.http
      .get<CollectionRevisionConnectionTargetResponse>(
        buildCollectionRevisionTargetConnectionUrl(this.apiRoot, exactIdentity),
        {
          context: withCollectionRequestContext(exactIdentity.collectionId),
        },
      )
      .pipe(
        map((response) =>
          decodeCollectionRevisionConnectionTargetResponse(
            response,
            exactIdentity,
          ),
        ),
      );
  }

  connect(
    identity: CollectionRevisionTargetRouteIdentity,
    request: ConnectCollectionRevisionTargetRequest,
  ): Observable<CollectionRevisionConnectionTargetDto> {
    const exactIdentity = requireTargetIdentity(identity);
    const connectionId = requireId(request.connection_id, 'connection_id');
    const entityId = requireId(request.entity_id, 'entity_id');
    const operation: ConnectIdempotentOperation = {
      kind: 'connect',
      ...exactIdentity,
      connectionId,
      entityId,
    };
    return this.mutate(
      operation,
      this.http.post<CollectionRevisionConnectionTargetResponse>(
        buildCollectionRevisionTargetConnectionsUrl(
          this.apiRoot,
          exactIdentity,
        ),
        {
          connection_id: connectionId,
          entity_id: entityId,
          credentials: { ...request.credentials },
        },
        { context: idempotentContext(operation) },
      ),
      exactIdentity,
      connectionId,
    ).pipe(
      catchError((error: unknown) => {
        if (isTargetAlreadyInProgress(error)) {
          return this.getConnectionTarget(exactIdentity);
        }
        return throwError(() => error);
      }),
    );
  }

  updateCredentials(
    identity: CollectionRevisionConnectionRouteIdentity,
    request: UpdateCollectionRevisionCredentialsRequest,
  ): Observable<CollectionRevisionConnectionTargetDto> {
    const exactIdentity = requireConnectionIdentity(identity);
    const operation = {
      kind: 'update-credentials',
      ...exactIdentity,
    } as const;
    return this.mutate(
      operation,
      this.http.put<CollectionRevisionConnectionTargetResponse>(
        buildCollectionRevisionConnectionCredentialsUrl(
          this.apiRoot,
          exactIdentity,
        ),
        { credentials: { ...request.credentials } },
        { context: idempotentContext(operation) },
      ),
      exactIdentity,
      exactIdentity.connectionId,
    );
  }

  getAuthInteraction(
    identity: CollectionRevisionAuthInteractionIdentity,
    pagination: { readonly page: number; readonly size: number },
  ): Observable<CollectionRevisionAuthInteractionDetailDto> {
    const exactIdentity = requireAuthIdentity(identity);
    const page = requirePositiveInteger(pagination.page, 'page');
    const size = requirePositiveInteger(pagination.size, 'size');
    return this.http
      .get<CollectionRevisionAuthInteractionDetailResponse>(
        buildCollectionRevisionConnectionAuthInteractionUrl(
          this.apiRoot,
          exactIdentity,
        ),
        {
          params: { page, size },
          context: withCollectionRequestContext(exactIdentity.collectionId),
        },
      )
      .pipe(
        map((response) => decodeAuthInteractionDetail(response, exactIdentity)),
      );
  }

  submitAuthInteraction(
    identity: CollectionRevisionAuthInteractionIdentity,
    request: SubmitCollectionRevisionAuthInteractionRequest,
  ): Observable<CollectionRevisionConnectionTargetDto> {
    const exactIdentity = requireAuthIdentity(identity);
    const operation = {
      ...exactIdentity,
      authInteractionKind: exactIdentity.kind,
      kind: 'submit-auth-interaction',
    } as const;
    return this.mutate(
      operation,
      this.http.post<CollectionRevisionConnectionTargetResponse>(
        buildCollectionRevisionConnectionAuthSubmitUrl(
          this.apiRoot,
          exactIdentity,
        ),
        {
          challenge_id: exactIdentity.challengeId,
          generation: exactIdentity.generation,
          kind: exactIdentity.kind,
          payload: { ...request.payload },
        },
        { context: idempotentContext(operation) },
      ),
      exactIdentity,
      exactIdentity.connectionId,
    );
  }

  resendAuthInteraction(
    identity: CollectionRevisionAuthInteractionIdentity,
  ): Observable<CollectionRevisionConnectionTargetDto> {
    const exactIdentity = requireAuthIdentity(identity);
    const operation = {
      ...exactIdentity,
      authInteractionKind: exactIdentity.kind,
      kind: 'resend-auth-interaction',
    } as const;
    return this.mutate(
      operation,
      this.http.post<CollectionRevisionConnectionTargetResponse>(
        buildCollectionRevisionConnectionAuthResendUrl(
          this.apiRoot,
          exactIdentity,
        ),
        {
          challenge_id: exactIdentity.challengeId,
          generation: exactIdentity.generation,
        },
        { context: idempotentContext(operation) },
      ),
      exactIdentity,
      exactIdentity.connectionId,
    );
  }

  cancelConnection(
    identity: CollectionRevisionConnectionRouteIdentity,
  ): Observable<CollectionRevisionConnectionTargetDto> {
    const exactIdentity = requireConnectionIdentity(identity);
    const operation = {
      kind: 'cancel-connection',
      ...exactIdentity,
    } as const;
    return this.mutate(
      operation,
      this.http.delete<CollectionRevisionConnectionTargetResponse>(
        buildCollectionRevisionConnectionDeleteUrl(this.apiRoot, exactIdentity),
        { context: idempotentContext(operation) },
      ),
      exactIdentity,
      exactIdentity.connectionId,
    );
  }

  private mutate(
    operation: CollectionIdempotentOperation,
    request: Observable<CollectionRevisionConnectionTargetResponse>,
    expected: CollectionRevisionTargetRouteIdentity,
    expectedConnectionId?: string,
  ): Observable<CollectionRevisionConnectionTargetDto> {
    return request.pipe(
      map((response) =>
        decodeCollectionRevisionConnectionTargetResponse(
          response,
          expected,
          expectedConnectionId,
        ),
      ),
      tap(() => this.idempotency.clear(operation)),
      catchError((error: unknown) => {
        if (isDefinitiveFailure(error)) this.idempotency.clear(operation);
        return throwError(() => error);
      }),
    );
  }
}

export function decodeCollectionRevisionConnectionTargetResponse(
  response: unknown,
  expected: CollectionRevisionTargetRouteIdentity,
  expectedConnectionId?: string,
): CollectionRevisionConnectionTargetDto {
  if (
    !isRecord(response) ||
    !hasOnlyKeys(response, CONNECTION_ENVELOPE_KEYS) ||
    !isRecord(response['connection_target'])
  ) {
    throw wireError('Exact connection response envelope is invalid.');
  }
  const applied = decodeAppliedFlag(response['applied']);
  const target = response['connection_target'];
  if (!hasOnlyKeys(target, CONNECTION_TARGET_KEYS)) {
    throw wireError('Exact connection target contains unknown fields.');
  }
  if (
    target['collection_id'] !== expected.collectionId ||
    target['revision_id'] !== expected.revisionId ||
    target['target_id'] !== expected.targetId
  ) {
    throw wireError(
      'Exact connection target identity does not match the route.',
    );
  }
  if (
    !isRevisionStatus(target['revision_status']) ||
    !isTargetStatus(target['target_status']) ||
    typeof target['editable'] !== 'boolean' ||
    !Array.isArray(target['allowed_actions']) ||
    !target['allowed_actions'].every(isConnectionTargetAction)
  ) {
    throw wireError('Exact connection target lifecycle is invalid.');
  }

  const configuredEntity = decodeNullableEntity(target['configured_entity']);
  const connection = decodeConnectionRuntime(target['connection']);
  const acquisition = decodeAcquisition(target['acquisition']);
  const allowedActions = target['allowed_actions'] as ConnectionTargetAction[];

  if (
    expectedConnectionId &&
    connection !== null &&
    connection.connection_id !== expectedConnectionId
  ) {
    throw wireError(
      'Connection identity does not match the exact command route.',
    );
  }
  const decoded = {
    ...(target as unknown as CollectionRevisionConnectionTargetDto),
    allowed_actions: allowedActions,
    configured_entity: configuredEntity,
    connection,
    acquisition,
    applied,
  };
  validateConnectionTargetRelationships(decoded);
  return decoded;
}

const CONNECTION_TARGET_KEYS = [
  'collection_id',
  'revision_id',
  'target_id',
  'revision_status',
  'target_status',
  'editable',
  'allowed_actions',
  'configured_entity',
  'connection',
  'acquisition',
] as const;

// The envelope stays closed: `applied` is a known key, not a relaxation. It is
// the lifecycle transition signal chordium emits as a sibling of
// `connection_target` on cancel, and it is absent on every read.
const CONNECTION_ENVELOPE_KEYS = ['connection_target', 'applied'] as const;

function decodeAppliedFlag(value: unknown): boolean | null {
  if (value === undefined) return null;
  if (typeof value !== 'boolean') {
    throw wireError('Exact connection response applied flag is invalid.');
  }
  return value;
}

const FIXED_ACTIONS_BY_CONNECTION_STATUS: Readonly<
  Partial<Record<ConnectionRuntimeStatus, readonly ConnectionTargetAction[]>>
> = Object.freeze({
  PENDING: ['CANCEL_CONNECTION'],
  AUTHENTICATING: ['CANCEL_CONNECTION'],
  ACTIVE: ['CANCEL_CONNECTION'],
  FAILED: ['UPDATE_CREDENTIALS', 'CANCEL_CONNECTION'],
  EXPIRED: ['UPDATE_CREDENTIALS', 'CANCEL_CONNECTION'],
});

function validateConnectionTargetRelationships(
  target: CollectionRevisionConnectionTargetDto,
): void {
  const { connection, configured_entity: configuredEntity } = target;
  if (target.target_status === 'PENDING' && connection !== null) {
    throw wireError('A pending target cannot expose a live connection.');
  }
  if (target.target_status === 'IN_PROGRESS' && connection === null) {
    throw wireError('An in-progress target requires a live connection.');
  }
  if (target.target_status === 'COMPLETED') {
    if (
      connection !== null ||
      target.acquisition === null ||
      target.editable ||
      target.allowed_actions.length > 0
    ) {
      throw wireError(
        'Completed targets cannot expose mutable connection state.',
      );
    }
    return;
  }
  if (target.acquisition !== null) {
    throw wireError(
      'Noncompleted targets cannot expose acquisition snapshots.',
    );
  }

  if (target.revision_status !== 'DRAFT' && target.editable) {
    throw wireError('Only a draft revision connection target can be editable.');
  }
  if (!target.editable) {
    if (target.allowed_actions.length > 0) {
      throw wireError('A read-only connection target cannot expose actions.');
    }
  } else {
    const expectedActions = expectedEditableActions(connection);
    if (!sameActions(target.allowed_actions, expectedActions)) {
      throw wireError(
        'Connection target actions contradict its runtime state.',
      );
    }
  }

  if (connection === null) return;
  if (
    configuredEntity !== null &&
    configuredEntity.id !== connection.entity.id
  ) {
    throw wireError(
      'Configured entity does not match the live connection entity.',
    );
  }
  const awaitingAuth = connection.status === 'AWAITING_AUTH_INTERACTION';
  if (awaitingAuth !== (connection.auth_interaction !== null)) {
    throw wireError(
      'Auth interaction summary does not match the connection status.',
    );
  }
}

function expectedEditableActions(
  connection: ConnectionRuntimeDto | null,
): readonly ConnectionTargetAction[] {
  if (connection === null) return ['CONNECT'];
  if (connection.status === 'NEEDS_CREDENTIALS') {
    return ['UPDATE_CREDENTIALS', 'CANCEL_CONNECTION'];
  }
  if (connection.status === 'AWAITING_AUTH_INTERACTION') {
    if (connection.auth_interaction === null) return [];
    return connection.auth_interaction.resend_supported
      ? [
          'SUBMIT_AUTH_INTERACTION',
          'RESEND_AUTH_INTERACTION',
          'CANCEL_CONNECTION',
        ]
      : ['SUBMIT_AUTH_INTERACTION', 'CANCEL_CONNECTION'];
  }
  return FIXED_ACTIONS_BY_CONNECTION_STATUS[connection.status] ?? [];
}

function sameActions(
  actual: readonly ConnectionTargetAction[],
  expected: readonly ConnectionTargetAction[],
): boolean {
  return (
    actual.length === expected.length &&
    actual.every((action, index) => action === expected[index])
  );
}

function decodeConnectionRuntime(value: unknown) {
  if (value === null) return null;
  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, [
      'connection_id',
      'status',
      'entity',
      'last_error',
      'auth_interaction',
      'latest_failure',
      'created_at',
    ]) ||
    !isNonEmptyString(value['connection_id']) ||
    !isConnectionStatus(value['status']) ||
    !isNonEmptyString(value['created_at'])
  ) {
    throw wireError('Connection runtime is invalid.');
  }
  const entity = decodePublicConnectionEntity(value['entity']);
  const authInteraction = decodeAuthSummary(value['auth_interaction']);
  decodePublicError(value['last_error']);
  decodeConnectionFailure(value['latest_failure']);
  return {
    ...(value as unknown as CollectionRevisionConnectionTargetDto['connection'] &
      object),
    entity,
    auth_interaction: authInteraction,
  };
}

function decodeAuthSummary(value: unknown): AuthInteractionDto | null {
  if (value === null) return null;
  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, [
      'challenge_id',
      'generation',
      'kind',
      'resend_supported',
    ]) ||
    !isNonEmptyString(value['challenge_id']) ||
    !Number.isInteger(value['generation']) ||
    !isAuthKind(value['kind']) ||
    typeof value['resend_supported'] !== 'boolean'
  ) {
    throw wireError('Auth interaction summary is invalid.');
  }
  return value as unknown as AuthInteractionDto;
}

function decodeAuthInteractionDetail(
  response: unknown,
  expected: CollectionRevisionAuthInteractionIdentity,
): CollectionRevisionAuthInteractionDetailDto {
  if (
    !isRecord(response) ||
    !hasOnlyKeys(response, ['auth_interaction']) ||
    !isRecord(response['auth_interaction'])
  ) {
    throw wireError('Auth interaction detail envelope is invalid.');
  }
  const detail = response['auth_interaction'];
  if (!hasOnlyKeys(detail, AUTH_DETAIL_KEYS)) {
    throw wireError('Auth interaction detail contains forbidden fields.');
  }
  if (
    detail['collection_id'] !== expected.collectionId ||
    detail['revision_id'] !== expected.revisionId ||
    detail['target_id'] !== expected.targetId ||
    detail['connection_id'] !== expected.connectionId ||
    detail['challenge_id'] !== expected.challengeId ||
    detail['generation'] !== expected.generation ||
    detail['kind'] !== expected.kind ||
    typeof detail['resend_supported'] !== 'boolean' ||
    !isNullableString(detail['title']) ||
    !isNullableString(detail['instructions']) ||
    !isNullableString(detail['expires_at']) ||
    !isNullableRecord(detail['payload_schema']) ||
    !isNullableAuthPage(detail['page']) ||
    !isRecord(detail['metadata']) ||
    !isOptionalString(detail['channel']) ||
    !isOptionalCodeFormat(detail['code_format']) ||
    !isOptionalString(detail['destination_hint']) ||
    !isOptionalString(detail['authenticator_label']) ||
    !isOptionalString(detail['resend_available_at']) ||
    // Retired by the backend; still accepted (and ignored) while it rolls out.
    !isOptionalInteger(detail['attempts_remaining']) ||
    !isOptionalLastError(detail['last_error']) ||
    !isOptionalString(detail['challenge_number'])
  ) {
    throw wireError('Auth interaction detail identity or payload is invalid.');
  }
  return detail as unknown as CollectionRevisionAuthInteractionDetailDto;
}

function isOptionalString(value: unknown): boolean {
  return value === undefined || isNullableString(value);
}

function isOptionalInteger(value: unknown): boolean {
  return value === undefined || value === null || Number.isInteger(value);
}

function isOptionalCodeFormat(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  return (
    isRecord(value) &&
    hasOnlyKeys(value, ['charset', 'length']) &&
    typeof value['charset'] === 'string' &&
    isOptionalInteger(value['length'])
  );
}

function isOptionalLastError(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  return (
    isRecord(value) &&
    hasOnlyKeys(value, ['code', 'retry_after_seconds', 'retry_available_at']) &&
    typeof value['code'] === 'string' &&
    isOptionalInteger(value['retry_after_seconds']) &&
    isOptionalString(value['retry_available_at'])
  );
}

const AUTH_DETAIL_KEYS = [
  'collection_id',
  'revision_id',
  'target_id',
  'connection_id',
  'challenge_id',
  'generation',
  'kind',
  'resend_supported',
  'title',
  'instructions',
  'expires_at',
  'payload_schema',
  'page',
  'metadata',
  'channel',
  'code_format',
  'destination_hint',
  'authenticator_label',
  'resend_available_at',
  'attempts_remaining',
  'last_error',
  'challenge_number',
] as const;

function decodePublicConnectionEntity(value: unknown): ConnectionEntityDto {
  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, [
      'id',
      'name',
      'display_name',
      'icon_url',
      'description',
      'credential_schema',
      'auth_capabilities',
      'require_waiver',
      'waiver_validity_days',
    ]) ||
    !isNonEmptyString(value['id']) ||
    !isNonEmptyString(value['name']) ||
    !isNonEmptyString(value['display_name']) ||
    !isNullableString(value['icon_url']) ||
    !isNullableString(value['description']) ||
    !isRecord(value['credential_schema']) ||
    !Array.isArray(value['auth_capabilities']) ||
    !value['auth_capabilities'].every(isRecord) ||
    typeof value['require_waiver'] !== 'boolean' ||
    typeof value['waiver_validity_days'] !== 'number'
  ) {
    throw wireError(
      'Public connection entity is invalid or exposes internals.',
    );
  }
  return value as unknown as ConnectionEntityDto;
}

function decodeNullableEntity(value: unknown): ConnectionEntityDto | null {
  return value === null ? null : decodePublicConnectionEntity(value);
}

function decodeAcquisition(value: unknown) {
  if (value === null) return null;
  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, [
      'connection_id',
      'entity',
      'sync_run_id',
      'outcome',
      'reason',
      'completed_at',
    ]) ||
    !isNonEmptyString(value['connection_id']) ||
    !isNullableNonEmptyString(value['sync_run_id']) ||
    !isNullableNonEmptyString(value['outcome']) ||
    !isNullableString(value['reason']) ||
    !isNonEmptyString(value['completed_at'])
  ) {
    throw wireError('Connection acquisition snapshot is invalid.');
  }
  decodeAcquisitionEntity(value['entity']);
  return value as unknown as CollectionRevisionConnectionTargetDto['acquisition'];
}

function decodeAcquisitionEntity(value: unknown): void {
  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, ['entity_id', 'entity_name', 'connection_status']) ||
    !isNullableNonEmptyString(value['entity_id']) ||
    !isNullableNonEmptyString(value['entity_name']) ||
    !isNonEmptyString(value['connection_status'])
  ) {
    throw wireError('Connection acquisition entity snapshot is invalid.');
  }
}

function decodePublicError(value: unknown): void {
  if (value === null) return;
  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, ['code', 'message', 'entity', 'details']) ||
    !isNonEmptyString(value['code']) ||
    typeof value['message'] !== 'string' ||
    !isNullableString(value['entity']) ||
    !isNullableRecord(value['details'])
  ) {
    throw wireError('Connection public error is invalid.');
  }
}

function decodeConnectionFailure(value: unknown): void {
  if (value === null) return;
  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, [
      'source',
      'job_id',
      'job_status',
      'occurred_at',
      'error',
    ]) ||
    !isNonEmptyString(value['source']) ||
    !isNonEmptyString(value['job_id']) ||
    !isNonEmptyString(value['job_status']) ||
    !isNonEmptyString(value['occurred_at'])
  ) {
    throw wireError('Connection failure projection is invalid.');
  }
  decodePublicError(value['error']);
}

function isNullableAuthPage(value: unknown): boolean {
  if (value === null) return true;
  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, [
      'page',
      'size',
      'total_items',
      'total_pages',
      'items',
    ]) ||
    !Number.isInteger(value['page']) ||
    !Number.isInteger(value['size']) ||
    !Number.isInteger(value['total_items']) ||
    !Number.isInteger(value['total_pages']) ||
    !Array.isArray(value['items'])
  ) {
    return false;
  }
  return value['items'].every(
    (item) =>
      isRecord(item) &&
      hasOnlyKeys(item, ['id', 'label', 'description', 'metadata']) &&
      isNonEmptyString(item['id']) &&
      typeof item['label'] === 'string' &&
      isNullableString(item['description']) &&
      isRecord(item['metadata']),
  );
}

function requireTargetIdentity(
  identity: CollectionRevisionTargetRouteIdentity,
): CollectionRevisionTargetRouteIdentity {
  return {
    collectionId: requireId(identity.collectionId, 'collectionId'),
    revisionId: requireId(identity.revisionId, 'revisionId'),
    targetId: requireId(identity.targetId, 'targetId'),
  };
}

function requireConnectionIdentity(
  identity: CollectionRevisionConnectionRouteIdentity,
): CollectionRevisionConnectionRouteIdentity {
  return {
    ...requireTargetIdentity(identity),
    connectionId: requireId(identity.connectionId, 'connectionId'),
  };
}

function requireAuthIdentity(
  identity: CollectionRevisionAuthInteractionIdentity,
): CollectionRevisionAuthInteractionIdentity {
  const generation = identity.generation;
  if (!Number.isInteger(generation) || generation < 1) {
    throw new TypeError('generation must be a positive integer');
  }
  if (!isAuthKind(identity.kind)) {
    throw new TypeError('kind must be a supported auth interaction kind');
  }
  return {
    ...requireConnectionIdentity(identity),
    challengeId: requireId(identity.challengeId, 'challengeId'),
    generation,
    kind: identity.kind,
  };
}

function idempotentContext(operation: CollectionIdempotentOperation) {
  return withCollectionRequestContext(operation.collectionId).set(
    COLLECTION_IDEMPOTENCY_OPERATION,
    operation,
  );
}

function isTargetAlreadyInProgress(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse) || error.status !== 409)
    return false;
  if (!isRecord(error.error)) return false;
  const nested = error.error['error'];
  return isRecord(nested) && nested['code'] === 'TARGET_ALREADY_IN_PROGRESS';
}

function isDefinitiveFailure(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) return true;
  return !(error.status === 0 || error.status === 408 || error.status >= 500);
}

function requirePositiveInteger(value: number, field: string): number {
  if (!Number.isInteger(value) || value < 1) {
    throw new TypeError(`${field} must be a positive integer`);
  }
  return value;
}

function requireId(value: string, field: string): string {
  if (!isNonEmptyString(value)) {
    throw new TypeError(`${field} must be a non-empty string`);
  }
  return value.trim();
}

function isRevisionStatus(value: unknown): boolean {
  return (
    value === 'DRAFT' ||
    value === 'SEALED' ||
    value === 'SUBMITTED' ||
    value === 'CANCELLED'
  );
}

function isTargetStatus(value: unknown): boolean {
  return (
    value === 'PENDING' || value === 'IN_PROGRESS' || value === 'COMPLETED'
  );
}

function isConnectionStatus(value: unknown): boolean {
  return (
    value === 'PENDING' ||
    value === 'AUTHENTICATING' ||
    value === 'AWAITING_AUTH_INTERACTION' ||
    value === 'NEEDS_CREDENTIALS' ||
    value === 'ACTIVE' ||
    value === 'FAILED' ||
    value === 'EXPIRED'
  );
}

function isAuthKind(value: unknown): boolean {
  return (
    value === 'TWO_FA_CODE' ||
    value === 'PUSH_APPROVAL' ||
    value === 'SCOPE_SELECTION'
  );
}

function isConnectionTargetAction(
  value: unknown,
): value is ConnectionTargetAction {
  return (
    typeof value === 'string' &&
    CONNECTION_TARGET_ACTIONS.has(value as ConnectionTargetAction)
  );
}

function hasOnlyKeys(
  value: Record<string, unknown>,
  allowed: readonly string[],
): boolean {
  const allowedSet = new Set(allowed);
  return Object.keys(value).every((key) => allowedSet.has(key));
}

function isNullableString(value: unknown): boolean {
  return value === null || typeof value === 'string';
}

function isNullableNonEmptyString(value: unknown): boolean {
  return value === null || isNonEmptyString(value);
}

function isNullableRecord(value: unknown): boolean {
  return value === null || isRecord(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function wireError(message: string): CollectionRevisionWireContractError {
  return new CollectionRevisionWireContractError(message);
}
